/**
 * Guardrails Contract
 *
 * Defines types for AI guardrails including input/output validation,
 * PII detection, topic control, and dialog rails.
 *
 * Issue #261: AI-Powered Data Chat Enhancement - Phase 1 (Guardrails)
 *
 * @see https://github.com/NVIDIA/NeMo-Guardrails - NeMo Guardrails reference
 * @see https://guardrailsai.com/ - Guardrails AI reference
 */

import { z } from 'zod';

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Types of guardrails that can be applied
 */
export enum GuardrailType {
  /** Jailbreak/prompt injection detection */
  JAILBREAK = 'JAILBREAK',
  /** Prompt injection attempt detection */
  PROMPT_INJECTION = 'PROMPT_INJECTION',
  /** Topic restriction enforcement */
  TOPIC_CONTROL = 'TOPIC_CONTROL',
  /** PII detection and redaction */
  PII_DETECTION = 'PII_DETECTION',
  /** Toxicity filtering */
  TOXICITY = 'TOXICITY',
  /** Hallucination check against RAG sources */
  HALLUCINATION = 'HALLUCINATION',
  /** Business rule compliance */
  COMPLIANCE = 'COMPLIANCE',
  /** Custom business-specific rail */
  CUSTOM = 'CUSTOM',
}

/**
 * Rail direction - when the rail is applied
 */
export enum RailDirection {
  /** Applied to user input before processing */
  INPUT = 'INPUT',
  /** Applied to AI output before returning */
  OUTPUT = 'OUTPUT',
  /** Applied during dialog/conversation flow */
  DIALOG = 'DIALOG',
  /** Applied to retrieval results */
  RETRIEVAL = 'RETRIEVAL',
}

/**
 * Action to take when a guardrail is triggered
 */
export enum GuardrailAction {
  /** Block the request/response entirely */
  BLOCK = 'BLOCK',
  /** Allow but flag for review */
  FLAG = 'FLAG',
  /** Automatically fix/redact problematic content */
  FIX = 'FIX',
  /** Allow with warning */
  WARN = 'WARN',
  /** Allow without modification */
  ALLOW = 'ALLOW',
}

/**
 * PII entity types that can be detected
 */
export enum PiiEntityType {
  PERSON = 'PERSON',
  EMAIL = 'EMAIL',
  PHONE_NUMBER = 'PHONE_NUMBER',
  SSN = 'SSN',
  CREDIT_CARD = 'CREDIT_CARD',
  ADDRESS = 'ADDRESS',
  DATE_OF_BIRTH = 'DATE_OF_BIRTH',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  PASSPORT = 'PASSPORT',
  DRIVER_LICENSE = 'DRIVER_LICENSE',
  IP_ADDRESS = 'IP_ADDRESS',
  MEDICAL_RECORD = 'MEDICAL_RECORD',
  CUSTOM = 'CUSTOM',
}

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Context for guardrail evaluation
 */
export interface GuardrailContext {
  /** Multi-tenant business ID (MANDATORY) */
  businessId: string;
  /** User making the request */
  userId?: string;
  /** Realm ID for schema isolation */
  realmId?: string;
  /** Session/conversation ID */
  sessionId?: string;
  /** Agent ID being invoked */
  agentId?: string;
  /** RAG sources for hallucination checking */
  ragSources?: RagSource[];
  /** Custom metadata for business rules */
  metadata?: Record<string, unknown>;
}

/**
 * RAG source for hallucination checking
 */
export interface RagSource {
  /** Source document ID */
  documentId: string;
  /** Source content */
  content: string;
  /** Relevance score */
  score: number;
  /** Source metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Detected PII entity
 */
export interface PiiEntity {
  /** Type of PII detected */
  type: PiiEntityType;
  /** Start position in text */
  start: number;
  /** End position in text */
  end: number;
  /** Original text (for logging, may be redacted) */
  text: string;
  /** Confidence score (0-1) */
  confidence: number;
  /** Redacted replacement text */
  redactedText?: string;
}

/**
 * Result of a single guardrail check
 */
export interface GuardrailCheckResult {
  /** Type of guardrail that was checked */
  type: GuardrailType;
  /** Whether the check passed */
  passed: boolean;
  /** Confidence score (0-1) */
  confidence: number;
  /** Action taken based on the check */
  action: GuardrailAction;
  /** Reason for the result */
  reason?: string;
  /** Detected entities (for PII detection) */
  detectedEntities?: PiiEntity[];
  /** Suggested fix if action is FIX */
  suggestedFix?: string;
  /** Processing time in milliseconds */
  durationMs: number;
}

/**
 * Result of guardrail validation (input or output)
 */
export interface GuardrailValidationResult {
  /** Whether the content passed all guardrails */
  passed: boolean;
  /** Action to take based on all checks */
  action: GuardrailAction;
  /** Sanitized content (after FIX actions applied) */
  sanitizedContent?: string;
  /** Individual check results */
  checks: GuardrailCheckResult[];
  /** Overall risk score (0-1) */
  riskScore: number;
  /** Human-readable message for blocked/flagged content */
  message?: string;
  /** Total processing time in milliseconds */
  totalDurationMs: number;
}

/**
 * Configuration for a guardrail
 */
export interface GuardrailConfig {
  /** Guardrail type */
  type: GuardrailType;
  /** Whether this guardrail is enabled */
  enabled: boolean;
  /** Threshold for triggering (0-1) */
  threshold?: number;
  /** Action to take when triggered */
  action: GuardrailAction;
  /** Custom configuration */
  config?: Record<string, unknown>;
}

/**
 * Input rail configuration
 */
export interface InputRailConfig {
  /** Enable jailbreak detection */
  jailbreakDetection?: GuardrailConfig;
  /** Enable prompt injection detection */
  promptInjectionDetection?: GuardrailConfig;
  /** Enable topic control */
  topicControl?: GuardrailConfig & {
    /** Allowed topics */
    allowedTopics?: string[];
    /** Blocked topics */
    blockedTopics?: string[];
  };
  /** Enable PII detection on input */
  piiDetection?: GuardrailConfig & {
    /** PII entity types to detect */
    entityTypes?: PiiEntityType[];
  };
}

/**
 * Output rail configuration
 */
export interface OutputRailConfig {
  /** Enable PII redaction in output */
  piiRedaction?: GuardrailConfig & {
    /** PII entity types to redact */
    entityTypes?: PiiEntityType[];
  };
  /** Enable toxicity filtering */
  toxicityFilter?: GuardrailConfig;
  /** Enable hallucination detection */
  hallucinationCheck?: GuardrailConfig & {
    /** Minimum RAG source overlap required */
    minSourceOverlap?: number;
  };
  /** Enable compliance validation */
  complianceValidation?: GuardrailConfig & {
    /** Business rules to enforce */
    rules?: string[];
  };
}

/**
 * Full guardrails configuration
 */
export interface GuardrailsConfig {
  /** Input rail configuration */
  inputRails: InputRailConfig;
  /** Output rail configuration */
  outputRails: OutputRailConfig;
  /** Default action for unhandled cases */
  defaultAction?: GuardrailAction;
  /** Enable detailed logging */
  enableDetailedLogging?: boolean;
}

// ============================================================================
// REQUEST/RESPONSE TYPES
// ============================================================================

/**
 * Request to validate input content
 */
export interface ValidateInputRequest {
  /** Content to validate */
  content: string;
  /** Guardrail context (MUST include businessId) */
  context: GuardrailContext;
  /** Optional specific rails to apply */
  rails?: GuardrailType[];
}

/**
 * Request to validate output content
 */
export interface ValidateOutputRequest {
  /** Content to validate */
  content: string;
  /** Guardrail context (MUST include businessId) */
  context: GuardrailContext;
  /** RAG sources for hallucination checking */
  ragSources?: RagSource[];
  /** Optional specific rails to apply */
  rails?: GuardrailType[];
}

/**
 * Request to detect PII in content
 */
export interface DetectPiiRequest {
  /** Content to scan */
  content: string;
  /** Business ID (MANDATORY for multi-tenancy) */
  businessId: string;
  /** Entity types to detect (defaults to all) */
  entityTypes?: PiiEntityType[];
  /** Whether to redact detected PII */
  redact?: boolean;
}

/**
 * Response from PII detection
 */
export interface DetectPiiResponse {
  /** Whether PII was detected */
  hasPii: boolean;
  /** Detected PII entities */
  entities: PiiEntity[];
  /** Redacted content (if redact=true) */
  redactedContent?: string;
  /** Processing time in milliseconds */
  durationMs: number;
}

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

export const guardrailContextSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  userId: z.string().optional(),
  realmId: z.string().optional(),
  sessionId: z.string().optional(),
  agentId: z.string().optional(),
  ragSources: z
    .array(
      z.object({
        documentId: z.string(),
        content: z.string(),
        score: z.number().min(0).max(1),
        metadata: z.record(z.string(), z.unknown()).optional(),
      }),
    )
    .optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const validateInputRequestSchema = z.object({
  content: z.string().min(1, 'Content is required'),
  context: guardrailContextSchema,
  rails: z.array(z.nativeEnum(GuardrailType)).optional(),
});

export const validateOutputRequestSchema = z.object({
  content: z.string().min(1, 'Content is required'),
  context: guardrailContextSchema,
  ragSources: z
    .array(
      z.object({
        documentId: z.string(),
        content: z.string(),
        score: z.number().min(0).max(1),
        metadata: z.record(z.string(), z.unknown()).optional(),
      }),
    )
    .optional(),
  rails: z.array(z.nativeEnum(GuardrailType)).optional(),
});

export const detectPiiRequestSchema = z.object({
  content: z.string().min(1, 'Content is required'),
  businessId: z.string().min(1, 'Business ID is required'),
  entityTypes: z.array(z.nativeEnum(PiiEntityType)).optional(),
  redact: z.boolean().optional().default(false),
});

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Default thresholds for guardrails
 */
export const DEFAULT_GUARDRAIL_THRESHOLDS = {
  [GuardrailType.JAILBREAK]: 0.8,
  [GuardrailType.PROMPT_INJECTION]: 0.7,
  [GuardrailType.TOPIC_CONTROL]: 0.6,
  [GuardrailType.PII_DETECTION]: 0.5,
  [GuardrailType.TOXICITY]: 0.7,
  [GuardrailType.HALLUCINATION]: 0.8,
  [GuardrailType.COMPLIANCE]: 0.9,
  [GuardrailType.CUSTOM]: 0.5,
} as const;

/**
 * Default guardrails configuration
 */
export const DEFAULT_GUARDRAILS_CONFIG: GuardrailsConfig = {
  inputRails: {
    jailbreakDetection: {
      type: GuardrailType.JAILBREAK,
      enabled: true,
      threshold: DEFAULT_GUARDRAIL_THRESHOLDS[GuardrailType.JAILBREAK],
      action: GuardrailAction.BLOCK,
    },
    promptInjectionDetection: {
      type: GuardrailType.PROMPT_INJECTION,
      enabled: true,
      threshold: DEFAULT_GUARDRAIL_THRESHOLDS[GuardrailType.PROMPT_INJECTION],
      action: GuardrailAction.BLOCK,
    },
    piiDetection: {
      type: GuardrailType.PII_DETECTION,
      enabled: true,
      threshold: DEFAULT_GUARDRAIL_THRESHOLDS[GuardrailType.PII_DETECTION],
      action: GuardrailAction.FLAG,
      entityTypes: [PiiEntityType.SSN, PiiEntityType.CREDIT_CARD, PiiEntityType.BANK_ACCOUNT],
    },
  },
  outputRails: {
    piiRedaction: {
      type: GuardrailType.PII_DETECTION,
      enabled: true,
      threshold: DEFAULT_GUARDRAIL_THRESHOLDS[GuardrailType.PII_DETECTION],
      action: GuardrailAction.FIX,
      entityTypes: [
        PiiEntityType.SSN,
        PiiEntityType.CREDIT_CARD,
        PiiEntityType.BANK_ACCOUNT,
        PiiEntityType.EMAIL,
        PiiEntityType.PHONE_NUMBER,
      ],
    },
    toxicityFilter: {
      type: GuardrailType.TOXICITY,
      enabled: true,
      threshold: DEFAULT_GUARDRAIL_THRESHOLDS[GuardrailType.TOXICITY],
      action: GuardrailAction.BLOCK,
    },
    hallucinationCheck: {
      type: GuardrailType.HALLUCINATION,
      enabled: true,
      threshold: DEFAULT_GUARDRAIL_THRESHOLDS[GuardrailType.HALLUCINATION],
      action: GuardrailAction.FLAG,
      minSourceOverlap: 0.3,
    },
  },
  defaultAction: GuardrailAction.ALLOW,
  enableDetailedLogging: true,
};

/**
 * Contract version for compatibility tracking
 */
export const GUARDRAILS_CONTRACT_VERSION = '1.0.0';
